<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <NotificationBox v-if="blockError" alertType="warning">{{ blockError }}</NotificationBox>

    <NotificationBox v-if="errorMessage" alertType="danger">{{ errorMessage }}</NotificationBox>

    <DetailBlock v-if="!blockError"
                 v-model:mode="panelMode"
                 :name="panelName"
                 :labels="blockLabels"
                 :entityKey="entityKey"
                 :entityTypeGuid="entityTypeGuid"
                 entityTypeName="InteractiveExperience"
                 :isAuditHidden="false"
                 :isBadgesVisible="true"
                 :isDeleteVisible="isEditable"
                 :isEditVisible="isEditable"
                 :isFollowVisible="false"
                 :isSecurityHidden="true"
                 @cancelEdit="onCancelEdit"
                 @delete="onDelete"
                 @edit="onEdit"
                 @save="onSave">
        <template #view>
            <ViewPanel :modelValue="interactiveExperienceViewBag" :options="options" />
        </template>

        <template #edit>
            <EditPanel v-model="interactiveExperienceEditBag" :options="options" @propertyChanged="onPropertyChanged" />
        </template>
    </DetailBlock>

    <ActionsPanel v-if="isActionListVisible"
                  v-model="interactiveExperienceActions"
                  :name="interactiveExperienceViewBag!.name!"
                  :interactiveExperienceIdKey="interactiveExperienceViewBag!.idKey!"
                  :actionTypes="actionTypes"
                  :visualizerTypes="visualizerTypes" />
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import NotificationBox from "@Obsidian/Controls/notificationBox.obs";
    import { EntityType } from "@Obsidian/SystemGuids/entityType";
    import DetailBlock from "@Obsidian/Templates/detailBlock";
    import { DetailPanelMode } from "@Obsidian/Enums/Controls/detailPanelMode";
    import { PanelAction } from "@Obsidian/Types/Controls/panelAction";
    import ActionsPanel from "./InteractiveExperienceDetail/actionsPanel.partial.obs";
    import EditPanel from "./InteractiveExperienceDetail/editPanel.partial.obs";
    import ViewPanel from "./InteractiveExperienceDetail/viewPanel.partial.obs";
    import { getSecurityGrant, provideSecurityGrant, refreshDetailAttributes, useConfigurationValues, useInvokeBlockAction } from "@Obsidian/Utility/block";
    import { debounce } from "@Obsidian/Utility/util";
    import { NavigationUrlKey } from "./InteractiveExperienceDetail/types.partial";
    import { DetailBlockBox } from "@Obsidian/ViewModels/Blocks/detailBlockBox";
    import { InteractiveExperienceBag } from "@Obsidian/ViewModels/Blocks/Event/InteractiveExperiences/InteractiveExperienceDetail/interactiveExperienceBag";
    import { InteractiveExperienceDetailOptionsBag } from "@Obsidian/ViewModels/Blocks/Event/InteractiveExperiences/InteractiveExperienceDetail/interactiveExperienceDetailOptionsBag";
    import { InteractiveExperienceActionTypeBag } from "@Obsidian/ViewModels/Blocks/Event/InteractiveExperiences/InteractiveExperienceDetail/interactiveExperienceActionTypeBag";
    import { ListItemBag } from "@Obsidian/ViewModels/Utility/listItemBag";

    const config = useConfigurationValues<DetailBlockBox<InteractiveExperienceBag, InteractiveExperienceDetailOptionsBag>>();
    const invokeBlockAction = useInvokeBlockAction();
    const securityGrant = getSecurityGrant(config.securityGrantToken);

    // #region Values

    const blockError = ref("");
    const errorMessage = ref("");

    const interactiveExperienceViewBag = ref(config.entity);
    const interactiveExperienceEditBag = ref<InteractiveExperienceBag>({} as InteractiveExperienceBag);
    const interactiveExperienceActions = ref(interactiveExperienceViewBag.value?.actions ?? []);

    const entityTypeGuid = EntityType.InteractiveExperience;

    const panelMode = ref<DetailPanelMode>(DetailPanelMode.View);

    // The properties that are being edited in the UI. This is used to
    // inform the server which incoming values have valid data in them.
    const validProperties: (keyof InteractiveExperienceBag)[] = [
        "attributeValues",
        "campusBehavior",
        "defaultCampus",
        "actionBackgroundColor",
        "actionBackgroundImageBinaryFile",
        "actionCustomCss",
        "actionPrimaryButtonColor",
        "actionPrimaryButtonTextColor",
        "actionSecondaryButtonColor",
        "actionSecondaryButtonTextColor",
        "actionTextColor",
        "audienceAccentColor",
        "audienceBackgroundColor",
        "audienceBackgroundImageBinaryFile",
        "audienceCustomCss",
        "audiencePrimaryColor",
        "audienceSecondaryColor",
        "audienceTextColor",
        "description",
        "isActive",
        "name",
        "noActionHeaderImageBinaryFile",
        "noActionMessage",
        "noActionTitle",
        "photoBinaryFile",
        "publicLabel",
        "pushNotificationType",
        "pushNotificationTitle",
        "pushNotificationDetail",
        "schedules",
        "welcomeHeaderImageBinaryFile",
        "welcomeMessage",
        "welcomeTitle"
    ];

    const refreshAttributesDebounce = debounce(() => refreshDetailAttributes(interactiveExperienceEditBag, validProperties, invokeBlockAction), undefined, true);

    // #endregion

    // #region Computed Values

    /**
     * The entity name to display in the block panel.
     */
    const panelName = computed((): string => {
        return interactiveExperienceViewBag.value?.name ?? "";
    });

    /**
     * The identifier key value for this entity.
     */
    const entityKey = computed((): string => {
        return interactiveExperienceViewBag.value?.idKey ?? "";
    });

    /**
     * Additional labels to display in the block panel.
     */
    const blockLabels = computed((): PanelAction[] | undefined => {
        const labels: PanelAction[] = [];

        if (panelMode.value !== DetailPanelMode.View) {
            return undefined;
        }

        if (interactiveExperienceViewBag.value?.isActive === true) {
            labels.push({
                iconCssClass: "fa fa-lightbulb",
                title: "Active",
                type: "success"
            });
        }
        else {
            labels.push({
                iconCssClass: "far fa-lightbulb",
                title: "Inactive",
                type: "danger"
            });
        }

        return labels;
    });

    const isEditable = computed((): boolean => {
        return config.isEditable === true;
    });

    const options = computed((): InteractiveExperienceDetailOptionsBag => {
        return config.options ?? {};
    });

    const isActionListVisible = computed((): boolean => {
        return !blockError.value && panelMode.value === DetailPanelMode.View && !!interactiveExperienceViewBag.value;
    });

    const actionTypes = computed((): InteractiveExperienceActionTypeBag[] => {
        return config.options?.actionTypes ?? [];
    });

    const visualizerTypes = computed((): ListItemBag[] => {
        return config.options?.visualizerTypes ?? [];
    });

    // #endregion

    // #region Functions

    // #endregion

    // #region Event Handlers

    /**
     * Event handler for the Cancel button being clicked while in Edit mode.
     * Handles redirect to parent page if creating a new entity.
     *
     * @returns true if the panel should leave edit mode; false if it should stay in edit mode; or a string containing a redirect URL.
     */
    async function onCancelEdit(): Promise<boolean | string> {
        if (!interactiveExperienceEditBag.value?.idKey) {
            if (config.navigationUrls?.[NavigationUrlKey.ParentPage]) {
                return config.navigationUrls[NavigationUrlKey.ParentPage];
            }

            return false;
        }

        return true;
    }

    /**
     * Event handler for the Delete button being clicked. Sends the
     * delete request to the server and then redirects to the target page.
     *
     * @returns false if it should stay on the page; or a string containing a redirect URL.
     */
    async function onDelete(): Promise<false | string> {
        errorMessage.value = "";

        const result = await invokeBlockAction<string>("Delete", {
            key: interactiveExperienceViewBag.value?.idKey
        });

        if (result.isSuccess && result.data) {
            return result.data;
        }
        else {
            errorMessage.value = result.errorMessage ?? "Unknown error while trying to delete interactive experience.";

            return false;
        }
    }

    /**
     * Event handler for the Edit button being clicked. Request the edit
     * details from the server and then enter edit mode.
     *
     * @returns true if the panel should enter edit mode; otherwise false.
     */
    async function onEdit(): Promise<boolean> {
        const result = await invokeBlockAction<DetailBlockBox<InteractiveExperienceBag, InteractiveExperienceDetailOptionsBag>>("Edit", {
            key: interactiveExperienceViewBag.value?.idKey
        });

        if (result.isSuccess && result.data && result.data.entity) {
            interactiveExperienceEditBag.value = result.data.entity;

            return true;
        }
        else {
            return false;
        }
    }

    /**
     * Event handler for when a value has changed that has an associated
     * C# property name. This is used to detect changes to values that
     * might cause qualified attributes to either show up or not show up.
     *
     * @param propertyName The name of the C# property that was changed.
     */
    function onPropertyChanged(propertyName: string): void {
        // If we don't have any qualified attribute properties or this property
        // is not one of them then do nothing.
        if (!config.qualifiedAttributeProperties || !config.qualifiedAttributeProperties.some(n => n.toLowerCase() === propertyName.toLowerCase())) {
            return;
        }

        refreshAttributesDebounce();
    }

    /**
     * Event handler for the panel's Save event. Send the data to the server
     * to be saved and then leave edit mode or redirect to target page.
     *
     * @returns true if the panel should leave edit mode; false if it should stay in edit mode; or a string containing a redirect URL.
     */
    async function onSave(): Promise<boolean | string> {
        errorMessage.value = "";

        const data: DetailBlockBox<InteractiveExperienceBag, InteractiveExperienceDetailOptionsBag> = {
            entity: interactiveExperienceEditBag.value,
            isEditable: true,
            validProperties: validProperties
        };

        const result = await invokeBlockAction<InteractiveExperienceBag | string>("Save", {
            box: data
        });

        if (result.isSuccess && result.data) {
            if (result.statusCode === 200 && typeof result.data === "object") {
                interactiveExperienceViewBag.value = result.data;
                interactiveExperienceActions.value = interactiveExperienceViewBag.value.actions ?? [];

                return true;
            }
            else if (result.statusCode === 201 && typeof result.data === "string") {
                return result.data;
            }
        }

        errorMessage.value = result.errorMessage ?? "Unknown error while trying to save interactive experience.";

        return false;
    }

    // #endregion

    provideSecurityGrant(securityGrant);

    // Handle any initial error conditions or the need to go into edit mode.
    if (config.errorMessage) {
        blockError.value = config.errorMessage;
    }
    else if (!config.entity) {
        blockError.value = "The specified interactive experience could not be viewed.";
    }
    else if (!config.entity.idKey) {
        interactiveExperienceEditBag.value = config.entity;
        panelMode.value = DetailPanelMode.Add;
    }
</script>
